import { useEffect, useState } from 'react';
import { forgetPlayerId } from '../lib/playerId';
import { supabase } from '../supabase';
import type { User, Session } from '@supabase/supabase-js';
import { isNative } from '../utils/platform';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  displayName: string;
  avatarUrl: string | null;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

export function useAuth(): AuthState {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      // Signing in turns a guest into an account, which is a different row in
      // users; a stale cache would keep reading the guest's history.
      forgetPlayerId();
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Handle deep link callback on native (app reopens with token) ──
  useEffect(() => {
    if (!isNative) return;

    // Import App plugin dynamically to avoid breaking web builds
    import('@capacitor/app').then(({ App }) => {
      App.addListener('appUrlOpen', async ({ url }) => {
        // Supabase sends tokens as hash fragments: #access_token=...&refresh_token=...
        if (url && (url.includes('access_token') || url.includes('refresh_token'))) {
          // Extract tokens from the URL hash/query
          const hashParams = new URLSearchParams(url.split('#')[1] || url.split('?')[1] || '');
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');

          if (accessToken && refreshToken) {
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            if (error) console.error('Deep link auth error:', error.message);
          }
        }
      });
    }).catch(() => {
      // @capacitor/app not available on web — ignore
    });
  }, []);

  async function signInWithGoogle() {
    if (isNative) {
      // Native: open OAuth in system browser, redirect back via deep link
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'app.worldoftrivia.wot://auth-callback',
          skipBrowserRedirect: true, // Don't redirect WebView, we'll handle it
        },
      });
      if (error) {
        console.error('Google sign-in error:', error.message);
        return;
      }
      if (data?.url) {
        // Open in system browser (Chrome/default browser)
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: data.url, windowName: '_system' });
      }
    } else {
      // Web: standard OAuth redirect
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) console.error('Google sign-in error:', error.message);
    }
  }

  async function signOut() {
    await supabase.auth.signOut();
    // The resolved player row is cached; signing out makes it the wrong one.
    forgetPlayerId();
    setUser(null);
    setSession(null);
  }

  // Extract display name from Google profile
  const meta = user?.user_metadata;
  const displayName = meta?.full_name || meta?.name || user?.email?.split('@')[0] || 'GUEST';
  const avatarUrl = meta?.avatar_url || meta?.picture || null;

  return { user, session, loading, displayName, avatarUrl, signInWithGoogle, signOut };
}
